import {
	listSetupRefusalReason,
	listSetupSourceIsEmpty,
	listSetupSourceState,
	ListSetupRefusalReason,
	ListSetupSources,
	ListSetupStatus,
	ListSourceState,
} from "@signalist/shared-types";

import { List } from "../schema/list";
import { listHasLeads, ListLeadCounts } from "./listHasLeads";

/**
 * How many rows each of the list's own source relations holds, under the name the payload carries
 * the array. The shared setup predicate reads these counts, so the web and the API judge a source
 * step from the same input (PREC-4537).
 */
export const listSetupSourcesOf = (list: List): ListSetupSources => ({
	jobs: list.jobs?.length ?? 0,
	maps: list.maps?.length ?? 0,
	liCompanyActivity: list.liCompanyActivity?.length ?? 0,
	liPeople: list.liPeople?.length ?? 0,
	liEvents: list.liEvents?.length ?? 0,
	liProfileVisits: list.liProfileVisits?.length ?? 0,
	liPosts: list.liPosts?.length ?? 0,
	csvEnrichments: list.csvEnrichments?.length ?? 0,
	parallelAi: list.parallelAi?.length ?? 0,
	liSn: list.liSn?.length ?? 0,
	companyEnriches: list.companyEnriches?.length ?? 0,
	customEvents: list.customEvents?.length ?? 0,
});

/**
 * Whether the list's own source step is still empty — no uploaded file, no saved search, no
 * scrape target for whatever kind of source this list type is built from. The decision, and which
 * relation array each source type reads, lives in the shared predicate.
 */
export const listSourceIsEmpty = (list: List): boolean =>
	listSetupSourceIsEmpty(list.sourceType, listSetupSourcesOf(list));

/**
 * What "this list has no source" actually means for the user (PREC-4118).
 *
 * `MISSING` and `MANUAL_ONLY` are the same database state — an upload list with no file — and the
 * UI used to treat both as "unconfigured", which is right for one of them and a lie about the
 * other:
 *
 * - `MISSING` — the list is empty too, so the source step genuinely was never finished. The
 *   overview's "Add source" call-to-action is the correct next step.
 * - `MANUAL_ONLY` — the list holds leads that were put there by hand (Contacts → Add to list, or
 *   moved in from another list; they arrive with no `csvEnrichmentPersonId` and no source name).
 *   Nothing disappeared and nothing is broken: BlueOak's list 6799 was built this way, 250 leads
 *   over three days, with a running campaign and a reply already in — while the overview reported
 *   it as unconfigured and greyed out its controls. A list that is being worked must not be
 *   described as unfinished.
 *
 * Lead counts arrive on a different payload than the source arrays, so they may be passed
 * separately: the overview's lean rows carry both, while the single full list (`GET list/:id`)
 * carries the source arrays and **no counts at all** — its caller hands in the lean list it
 * already holds.
 */
export type { ListSourceState };

export const getListSourceState = (
	list: List,
	leadCounts: ListLeadCounts | null | undefined = list
): ListSourceState =>
	listSetupSourceState({
		sourceType: list.sourceType,
		sources: listSetupSourcesOf(list),
		hasLeads: listHasLeads(leadCounts),
	});

/**
 * Why the shared setup decision refuses activation, from the flags the adapter just returned
 * (PREC-4537). The Activate toggle is disabled exactly when this is not null, and the reason names
 * the step the user has to finish.
 */
export const getListSetupRefusal = (
	flags: Omit<ListSetupStatus, "sourceState" | "activation">
): ListSetupRefusalReason | null => {
	const decision = listSetupRefusalReason(flags);

	return decision.allowed ? null : decision.reason;
};

/** The sentence a disabled Activate control shows for a setup refusal. */
export const describeListSetupRefusal = (
	reason: ListSetupRefusalReason,
	t: (key: string) => string
): string => {
	switch (reason) {
		case "LIST_SETUP_SCRAPE_NOT_REQUESTED":
			return t("errors.listSetupScrapeNotRequested");
		case "LIST_SETUP_SCRAPE_ONGOING":
			return t("errors.listSetupScrapeOngoing");
		case "LIST_SETUP_SCRAPE_UNCONFIRMED":
			return t("errors.listSetupScrapeUnconfirmed");
		case "LIST_SETUP_SOURCE_MISSING":
			return t("errors.listSetupSourceMissing");
		case "LIST_SETUP_SOURCE_MANUAL_ONLY":
			// PREC-4118 keeps the richer "no file to run on" wording for a hand-built list.
			return t("list.noFile.cannotRun");
		case "LIST_SETUP_SIGNALS_MISSING":
			return t("errors.listSetupSignalsMissing");
		case "LIST_SETUP_FIND_PERSON_MISSING":
			return t("errors.listSetupFindPersonMissing");
		default: {
			// A new refusal code is a type error here rather than a silently missing sentence.
			const exhaustive: never = reason;

			return t(`errors.${String(exhaustive)}`);
		}
	}
};
